import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getAnnouncement } from '@ebalance/proto/bffuser/v1/announcement_service-AnnouncementService_connectquery'
import {
  GetAnnouncementRequestSchema,
  type GetAnnouncementResponse,
} from '@ebalance/proto/bffuser/v1/announcement_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * 詳細画面で「見つからない」として画面内に描く失敗。
 *
 * InvalidArgument を含めるのは、この RPC で InvalidArgument を返す経路が request の
 * id に対する protovalidate (英数字と . _ -、64 文字以内) だけで、その形式に合わない
 * URL の id は存在する告知を指しえないため。共有 URL の typo は形式違反にもなりうるので、
 * NotFound と同じく一覧への導線を出す (Design Note D-UI-7)。
 */
export function isAnnouncementNotFound(error: unknown): boolean {
  return (
    error instanceof ConnectError &&
    (error.code === Code.NotFound || error.code === Code.InvalidArgument)
  )
}

/**
 * BFF AnnouncementService.GetAnnouncement を取得する hook。
 *
 * NotFound と id 形式違反の InvalidArgument だけは error boundary に投げず呼び出し側で
 * 「見つからない」状態を描く (共有 URL の typo や取り下げで利用者が普通に踏む状態で、
 * 一覧への導線が要る。`isAnnouncementNotFound` 参照)。
 * それ以外の失敗は本番 QueryClient の既定 (`throwOnError: true`) と同じく throw
 * する。boolean の `false` で上書きしないのは、技術失敗まで画面内に抱えて
 * 「見つからない」と誤表示しないため。
 */
export function useGetAnnouncement(
  announcementId: string | undefined,
): UseQueryResult<GetAnnouncementResponse, ConnectError> {
  return useQuery(
    getAnnouncement,
    create(GetAnnouncementRequestSchema, {
      announcementId: announcementId ?? '',
    }),
    {
      enabled: announcementId !== undefined && announcementId !== '',
      throwOnError: (error) => !isAnnouncementNotFound(error),
    },
  )
}
